import { Box, SimpleGrid, Wrap, WrapItem } from '@chakra-ui/react';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useState } from 'react';

import Link from 'components/Link';
import Text from 'components/Text';

// Material Design color palette
const MATERIAL_COLORS: Record<string, Record<string, string>> = {
  Red: {
    '50': '#FFEBEE', '100': '#FFCDD2', '200': '#EF9A9A', '300': '#E57373',
    '400': '#EF5350', '500': '#F44336', '600': '#E53935', '700': '#D32F2F',
    '800': '#C62828', '900': '#B71C1C',
  },
  Pink: {
    '50': '#FCE4EC', '100': '#F8BBD0', '200': '#F48FB1', '300': '#F06292',
    '400': '#EC407A', '500': '#E91E63', '600': '#D81B60', '700': '#C2185B',
    '800': '#AD1457', '900': '#880E4F',
  },
  Purple: {
    '50': '#F3E5F5', '100': '#E1BEE7', '200': '#CE93D8', '300': '#BA68C8',
    '400': '#AB47BC', '500': '#9C27B0', '600': '#8E24AA', '700': '#7B1FA2',
    '800': '#6A1B9A', '900': '#4A148C',
  },
  'Deep Purple': {
    '50': '#EDE7F6', '100': '#D1C4E9', '200': '#B39DDB', '300': '#9575CD',
    '400': '#7E57C2', '500': '#673AB7', '600': '#5E35B1', '700': '#512DA8',
    '800': '#4527A0', '900': '#311B92',
  },
  Indigo: {
    '50': '#E8EAF6', '100': '#C5CAE9', '200': '#9FA8DA', '300': '#7986CB',
    '400': '#5C6BC0', '500': '#3F51B5', '600': '#3949AB', '700': '#303F9F',
    '800': '#283593', '900': '#1A237E',
  },
  Blue: {
    '50': '#E3F2FD', '100': '#BBDEFB', '200': '#90CAF9', '300': '#64B5F6',
    '400': '#42A5F5', '500': '#2196F3', '600': '#1E88E5', '700': '#1976D2',
    '800': '#1565C0', '900': '#0D47A1',
  },
  'Light Blue': {
    '50': '#E1F5FE', '100': '#B3E5FC', '200': '#81D4FA', '300': '#4FC3F7',
    '400': '#29B6F6', '500': '#03A9F4', '600': '#039BE5', '700': '#0288D1',
    '800': '#0277BD', '900': '#01579B',
  },
  Cyan: {
    '50': '#E0F7FA', '100': '#B2EBF2', '200': '#80DEEA', '300': '#4DD0E1',
    '400': '#26C6DA', '500': '#00BCD4', '600': '#00ACC1', '700': '#0097A7',
    '800': '#00838F', '900': '#006064',
  },
  Teal: {
    '50': '#E0F2F1', '100': '#B2DFDB', '200': '#80CBC4', '300': '#4DB6AC',
    '400': '#26A69A', '500': '#009688', '600': '#00897B', '700': '#00796B',
    '800': '#00695C', '900': '#004D40',
  },
  Green: {
    '50': '#E8F5E9', '100': '#C8E6C9', '200': '#A5D6A7', '300': '#81C784',
    '400': '#66BB6A', '500': '#4CAF50', '600': '#43A047', '700': '#388E3C',
    '800': '#2E7D32', '900': '#1B5E20',
  },
  'Light Green': {
    '50': '#F1F8E9', '100': '#DCEDC8', '200': '#C5E1A5', '300': '#AED581',
    '400': '#9CCC65', '500': '#8BC34A', '600': '#7CB342', '700': '#689F38',
    '800': '#558B2F', '900': '#33691E',
  },
  Lime: {
    '50': '#F9FBE7', '100': '#F0F4C3', '200': '#E6EE9C', '300': '#DCE775',
    '400': '#D4E157', '500': '#CDDC39', '600': '#C0CA33', '700': '#AFB42B',
    '800': '#9E9D24', '900': '#827717',
  },
  Yellow: {
    '50': '#FFFDE7', '100': '#FFF9C4', '200': '#FFF59D', '300': '#FFF176',
    '400': '#FFEE58', '500': '#FFEB3B', '600': '#FDD835', '700': '#FBC02D',
    '800': '#F9A825', '900': '#F57F17',
  },
  Amber: {
    '50': '#FFF8E1', '100': '#FFECB3', '200': '#FFE082', '300': '#FFD54F',
    '400': '#FFCA28', '500': '#FFC107', '600': '#FFB300', '700': '#FFA000',
    '800': '#FF8F00', '900': '#FF6F00',
  },
  Orange: {
    '50': '#FFF3E0', '100': '#FFE0B2', '200': '#FFCC80', '300': '#FFB74D',
    '400': '#FFA726', '500': '#FF9800', '600': '#FB8C00', '700': '#F57C00',
    '800': '#EF6C00', '900': '#E65100',
  },
  'Deep Orange': {
    '50': '#FBE9E7', '100': '#FFCCBC', '200': '#FFAB91', '300': '#FF8A65',
    '400': '#FF7043', '500': '#FF5722', '600': '#F4511E', '700': '#E64A19',
    '800': '#D84315', '900': '#BF360C',
  },
  Brown: {
    '50': '#EFEBE9', '100': '#D7CCC8', '200': '#BCAAA4', '300': '#A1887F',
    '400': '#8D6E63', '500': '#795548', '600': '#6D4C41', '700': '#5D4037',
    '800': '#4E342E', '900': '#3E2723',
  },
  Grey: {
    '50': '#FAFAFA', '100': '#F5F5F5', '200': '#EEEEEE', '300': '#E0E0E0',
    '400': '#BDBDBD', '500': '#9E9E9E', '600': '#757575', '700': '#616161',
    '800': '#424242', '900': '#212121',
  },
  'Blue Grey': {
    '50': '#ECEFF1', '100': '#CFD8DC', '200': '#B0BEC5', '300': '#90A4B7',
    '400': '#78909C', '500': '#607D8B', '600': '#546E7A', '700': '#455A64',
    '800': '#37474F', '900': '#263238',
  },
};

const COLOR_HUES = Object.keys(MATERIAL_COLORS);
const SHADE_KEYS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  return { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) };
};

const hexToHsl = (hex: string) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

const isLightColor = (hex: string) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.6;
};

const ColorsTab = () => {
  const [copyFormat, setCopyFormat] = useState<'hex' | 'rgb' | 'hsl'>('hex');
  const { toastSuccess } = useToastWrapper();

  const formatColor = (hex: string) => {
    switch (copyFormat) {
      case 'rgb': {
        const rgb = hexToRgb(hex);
        return rgb ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : hex;
      }
      case 'hsl': {
        const hsl = hexToHsl(hex);
        return hsl ? `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` : hex;
      }
      default:
        return hex;
    }
  };

  const handleCopyColor = (hex: string) => {
    const formatted = formatColor(hex);
    navigator.clipboard.writeText(formatted);
    toastSuccess(`Copied ${formatted}`);
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Text fontSize="large" fontWeight="semibold" mb={2}>
          Color Palette
        </Text>
        <Text fontSize="small" opacity="secondary">
          Click to copy. Colors from{' '}
          <Link href="https://m2.material.io/design/color/the-color-system.html" isExternal fontSize="small">
            Material Design
          </Link>
          .
        </Text>
      </Box>

      {/* Format toggle */}
      <Wrap spacing={2} mb={4}>
        <WrapItem>
          <Text fontSize="small" opacity="secondary" lineHeight="28px">
            Format:
          </Text>
        </WrapItem>
        {(['hex', 'rgb', 'hsl'] as const).map((format) => (
          <WrapItem key={format}>
            <Box
              px={3}
              py={1}
              borderRadius="full"
              fontSize="small"
              cursor="pointer"
              bg={copyFormat === format ? 'purple.500' : 'whiteAlpha.200'}
              _hover={{ bg: copyFormat === format ? 'purple.400' : 'whiteAlpha.300' }}
              transition="all 0.15s"
              onClick={() => setCopyFormat(format)}
              textTransform="uppercase"
            >
              {format}
            </Box>
          </WrapItem>
        ))}
      </Wrap>

      {/* Color grid - all hues as rows */}
      <Box>
        {COLOR_HUES.map((hue) => (
          <SimpleGrid key={hue} columns={10} spacing="2px" mb="2px">
            {SHADE_KEYS.map((shade) => {
              const hex = MATERIAL_COLORS[hue][shade];
              return (
                <Box
                  key={`${hue}-${shade}`}
                  bg={hex}
                  height="28px"
                  cursor="pointer"
                  _hover={{ transform: 'scale(1.15)', zIndex: 1, boxShadow: 'lg' }}
                  transition="all 0.1s"
                  title={`${hue} ${shade}: ${hex}`}
                  onClick={() => handleCopyColor(hex)}
                  position="relative"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    as="span"
                    fontWeight="bold"
                    color={isLightColor(hex) ? 'blackAlpha.600' : 'whiteAlpha.600'}
                    opacity={0}
                    _groupHover={{ opacity: 1 }}
                    userSelect="none"
                    style={{ fontSize: '6px' }}
                  >
                    {shade}
                  </Box>
                </Box>
              );
            })}
          </SimpleGrid>
        ))}
      </Box>

      {/* More resources */}
      <Box mt={4} pt={4} borderTopWidth="1px" borderColor="whiteAlpha.200">
        <Text fontSize="small" opacity="secondary">
          More palettes:{' '}
          <Link href="https://colorhunt.co" isExternal fontSize="small">
            Color Hunt
          </Link>
          {' | '}
          <Link href="https://coolors.co" isExternal fontSize="small">
            Coolors
          </Link>
          {' | '}
          <Link href="https://v2.chakra-ui.com/docs/styled-system/theme#colors" isExternal fontSize="small">
            Chakra UI
          </Link>
        </Text>
      </Box>
    </Box>
  );
};

export default ColorsTab;
